import { BehaviorSubject, forkJoin, of, throwError } from 'rxjs';
import { timeout, map, catchError, switchMap, tap } from 'rxjs/operators';

import { Store } from '@ngxs/store';
import { LocalStorage } from 'ngx-webstorage';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '@env/environment';

import { AccountState } from '@shared/states/account.state';
import { AuthState } from '@shared/states/auth.state';
import { AIModelDependentFunctionResult, AIModelDependentMessage } from '@shared/utilities/string.utilities';
import { PrefsState } from '@shared/states/prefs.state';
import { DialogControl } from '@shared/services/dialog-control.service';

/**
 * This service will calculate z-scores for the individual users
 */
@Injectable({
  providedIn: 'root',
})
export class ReportAssistant {
  @LocalStorage('consent') trackingAllowed: boolean | undefined;

  constructor(
    private dc: DialogControl,
    private store: Store,
    private http: HttpClient,
  ) {}

  public getClusters(surveyKey: string, reportKey: string, language: string, items: any, backgroundInfo: string) {
    const teamKey = this.store.selectSnapshot(AccountState.teamKey);
    const authToken: string = this.store.selectSnapshot(AuthState.authToken);

    const url: string = `https:${environment.apiServer}/ai/esko/whyFinderThemes`;
    const itemsCount = items?.strengths?.length + items?.weaknesses?.length;
    let strPrcnt = items?.strengths?.length / itemsCount;
    strPrcnt = isNaN(strPrcnt) ? 0.5 : strPrcnt;

    const headers = new HttpHeaders()
      .set('Environment', environment.config)
      .set('Active-Team', teamKey || '')
      .set('Target-Report', reportKey || '')
      .set('Survey-Key', surveyKey || '')
      .set('Content-Type', 'application/json')
      .set('Firebase-Authorization', authToken || '');

    const request: (k: number) => any = (k) =>
      this.http
        .post(
          url,
          {
            temperature: 0.2,
            // model: 3,
            modelKey: 'gpt-4o-mini',
            seed: 1,
            functionName: 'getWhyFinderThemes',
            messages: [
              {
                role: 'user',
                message: JSON.stringify({
                  strengths: items?.strengths
                    ?.map((v) => [v, Math.random()])
                    .sort((a: [any, number], b: [any, number]) => a[1] - b[1])
                    .map((v) => v[0])
                    .slice(0, 100 * strPrcnt),
                  weaknesses: items?.weaknesses
                    ?.map((v) => [v, Math.random()])
                    .sort((a: [any, number], b: [any, number]) => a[1] - b[1])
                    .map((v) => v[0])
                    .slice(0, 100 * (1 - strPrcnt)),
                }),
              },
            ],
            parameters: {
              system: [language, backgroundInfo, k],
              function: [language],
            },
          },
          {
            headers,
            withCredentials: this.trackingAllowed || false,
          },
        )
        .pipe(
          timeout(300000),
          catchError((error) => throwError(() => this.handleRequestError(error))),
        );

    const requests =
      itemsCount > 100
        ? Array.from({ length: 7 }, () => request(10))
        : itemsCount > 75
          ? Array.from({ length: 4 }, () => request(7))
          : itemsCount > 20
            ? Array.from({ length: 3 }, () => request(5))
            : Array.from({ length: 3 }, () => request(3));

    return forkJoin(requests);
  }

  public encode_texts(surveyKey: string, reportKey: string, items: string[]) {
    const teamKey = this.store.selectSnapshot(AccountState.teamKey);
    const authToken: string = this.store.selectSnapshot(AuthState.authToken);

    const url: string = `https:${environment.apiServer}/ai/semantic/encode_text`;

    const headers = new HttpHeaders()
      .set('Environment', environment.config)
      .set('Active-Team', teamKey || '')
      .set('Target-Report', reportKey || '')
      .set('Survey-Key', surveyKey || '')
      .set('Content-Type', 'application/json')
      .set('Firebase-Authorization', authToken || '');

    return this.http
      .post(
        url,
        {
          data: {
            items,
          },
        },
        {
          headers,
          withCredentials: this.trackingAllowed || false,
        },
      )
      .pipe(timeout(300000));
  }

  public finalizeClusters(
    surveyKey: string,
    reportKey: string,
    language: string,
    items: string,
    backgroundInfo: string,
  ) {
    const url: string = `https:${environment.apiServer}/ai/esko/whyFinderFinalizedThemes`;
    const data: { [prop: string]: any } = {
      temperature: 0.4,
      modelKey: 'gpt-4o',
      functionName: 'getWhyFinderFinalizedThemes',
      messages: [{ role: 'user', message: items }],
      parameters: {
        function: [language, backgroundInfo],
        system: [language, backgroundInfo],
      },
    };

    return this.http
      .post(url, data, {
        headers: this.setHeaders(reportKey, surveyKey),
        withCredentials: this.trackingAllowed || false,
      })
      .pipe(
        timeout(300000),
        catchError((error) => throwError(() => this.handleRequestError(error))),
      );
  }

  public getImportance(reportKey: string, language: string, items: string, backgroundInfo: string) {
    const teamKey = this.store.selectSnapshot(AccountState.teamKey);
    const authToken: string = this.store.selectSnapshot(AuthState.authToken);

    const url: string = `https:${environment.apiServer}/ai/esko/whyFinderImportances`;
    const data: { [prop: string]: any } = {
      temperature: 0.7,
      model: 4,
      functionName: 'getWhyFinderImportances',
      messages: [{ role: 'user', message: items }],
      parameters: {
        function: [language, backgroundInfo],
        system: [language, backgroundInfo],
      },
    };

    const headers = new HttpHeaders()
      .set('Environment', environment.config)
      .set('Active-Team', teamKey || '')
      .set('Target-Report', reportKey || '')
      .set('Content-Type', 'application/json')
      .set('Firebase-Authorization', authToken || '');

    return this.http
      .post(url, data, {
        headers,
        withCredentials: this.trackingAllowed || false,
      })
      .pipe(
        timeout(300000),
        catchError((error) => throwError(() => this.handleRequestError(error))),
      );
  }

  public reportGridRecommendation(
    surveyKey: string,
    reportKey: string,
    language: string,
    gridItems: string,
    filters?: string,
  ) {
    const companyProfile = this.store.selectSnapshot(PrefsState.properties)?.orgDescription || '';
    const userProfile =
      (this.store.selectSnapshot(PrefsState.properties)?.description || `User doesn't have a profile`) +
      (companyProfile ? `\n\nUser's organization: ${companyProfile}` : '');
    const url: string = `https:${environment.apiServer}/ai/esko/reportGridRecommendation`;
    const data: { [prop: string]: any } = {
      temperature: 0.4,
      maxTokens: 8096,
      modelKey: 'vertexai-claude-3-7-sonnet@20250219',
      secondaryModelKey: 'gpt-4o',
      functionName: 'getReportGridRecommendation',
      messages: [{ role: 'user', message: gridItems }],
      parameters: {
        user: [language, gridItems, userProfile, filters],
      },
    };

    return this.http
      .post(url, data, {
        headers: this.setHeaders(reportKey, surveyKey),
        withCredentials: this.trackingAllowed || false,
      })
      .pipe(
        timeout(300000),
        map(AIModelDependentFunctionResult),
        map((res: any) => {
          const result = res?.reportGrid;
          if (typeof result === 'string') {
            try {
              return JSON.parse(result);
            } catch (e) {
              console.log(e);
              return {};
            }
          } else {
            return result;
          }
        }),
        catchError(() => of({})),
      );
  }

  public reportExecutiveSummary(
    surveyKey: string,
    reportKey: string,
    language: string,
    materials: any,
    loadingPhase: BehaviorSubject<string>,
    individualPart?: number,
    existingExecutiveSummary?: { [prop: string]: string },
  ) {
    const executiveSummary = existingExecutiveSummary || {};
    const companyProfile = this.store.selectSnapshot(PrefsState.properties)?.orgDescription || '';
    const userProfile =
      (this.store.selectSnapshot(PrefsState.properties)?.description || `User doesn't have a profile`) +
      (companyProfile ? `\n\nUser's organization: ${companyProfile}` : '');
    const surveyDetails = materials?.surveyDetails || {};
    const questions =
      materials?.questions?.[surveyKey]?.map(
        ({ key, title, type, choiceList, sliderLabels, sliderLablelsX, sliderLabelsY }) => ({
          key,
          title,
          type,
          ...(choiceList && choiceList.length ? { choiceList: choiceList.map((c) => c.content) } : {}),
          ...(sliderLabels?.min || sliderLabels?.max || sliderLabels?.axis
            ? { sliderLabels: { min: sliderLabels.min, max: sliderLabels.max, axis: sliderLabels.axis } }
            : {}),
          ...(sliderLablelsX?.min || sliderLablelsX?.max || sliderLablelsX?.axis
            ? { sliderLablelsX: { min: sliderLablelsX.min, max: sliderLablelsX.max, axis: sliderLablelsX.axis } }
            : {}),
          ...(sliderLabelsY?.min || sliderLabelsY?.max || sliderLabelsY?.axis
            ? { sliderLabelsY: { min: sliderLabelsY.min, max: sliderLabelsY.max, axis: sliderLabelsY.axis } }
            : {}),
        }),
      ) || [];
    const responseRates = materials?.responseRates || {};
    const timeline = materials?.gridItems?.filter((item) => item.key === 'time') || {};
    const demographics = materials?.gridItems?.filter((item) => item?.info?.includes('Demographics')) || {};

    const request = (part: number, executiveSummary) => {
      const promptName = [
        'reportExecutiveSummaryMethodology',
        'reportExecutiveSummaryKeyFindings',
        'reportExecutiveSummaryRecommendations',
        'reportExecutiveSummaryConclusion',
        'reportExecutiveSummaryAbstract',
      ];
      const prop = ['methodologies', 'keyFindings', 'recommendations', 'conclusion', 'abstract'];
      const parameters = [
        {
          system: [language],
          user: [
            language,
            userProfile,
            JSON.stringify(surveyDetails) || '',
            JSON.stringify(questions) || '',
            JSON.stringify(responseRates) || '',
            JSON.stringify(timeline) || '',
            JSON.stringify(demographics) || '',
            materials?.filters,
          ],
        },
        {
          system: [language],
          user: [
            language,
            userProfile,
            executiveSummary.methodologies || '',
            JSON.stringify(materials?.gridItems) || '',
            materials?.filters,
          ],
        },
        {
          system: [language],
          user: [language, userProfile, executiveSummary.methodologies || '', executiveSummary.keyFindings || ''],
        },
        {
          system: [language],
          user: [
            language,
            userProfile,
            executiveSummary.methodologies || '',
            executiveSummary.keyFindings || '',
            executiveSummary.recommendations || '',
          ],
        },
        {
          system: [language],
          user: [
            language,
            userProfile,
            executiveSummary.methodologies || '',
            executiveSummary.keyFindings || '',
            executiveSummary.recommendations || '',
            executiveSummary.conclusion,
          ],
        },
      ];
      const actionName = [
        $localize`Analyzing used methodologies... (1/5)`,
        $localize`Searching key findings from the charts... (2/5)`,
        $localize`Constructing recommendations from key findings... (3/5)`,
        $localize`Summarizing everything for the final conclusions... (4/5)`,
        $localize`Writing opening words... (5/5)`,
      ];
      const url: string = `https:${environment.apiServer}/ai/esko/${promptName[part]}`;
      const data: { [prop: string]: any } = {
        temperature: 0.4,
        maxTokens: 8096,
        modelKey: 'vertexai-claude-3-7-sonnet@20250219',
        secondaryModelKey: 'gpt-4o',
        parameters: parameters[part],
      };
      loadingPhase.next(actionName[part]);

      return this.http
        .post(url, data, {
          headers: this.setHeaders(reportKey, surveyKey),
          withCredentials: this.trackingAllowed || false,
        })
        .pipe(
          timeout(300000),
          map(AIModelDependentMessage),
          tap((res) => {
            executiveSummary[prop[part]] = res;
          }),
          map((res) => (!individualPart && part === 4 ? executiveSummary : res)),
          catchError(() => of({ error: $localize`Oops... Something went wrong, please try again.` })),
        );
    };

    if (!individualPart) {
      return request(0, executiveSummary).pipe(
        switchMap(() =>
          request(1, executiveSummary).pipe(
            switchMap(() =>
              request(2, executiveSummary).pipe(
                switchMap(() => request(3, executiveSummary).pipe(switchMap(() => request(4, executiveSummary)))),
              ),
            ),
          ),
        ),
      );
    } else {
      return request(individualPart, executiveSummary);
    }
  }

  public getWhyFinderTextSummary(
    surveyKey: string,
    reportKey: string,
    language: string,
    backgroundInfo: string,
    message: { role: string; message: string },
    baseStateMessages: { role: string; message: string }[] = [],
  ) {
    const url: string = `https:${environment.apiServer}/ai/esko/${
      baseStateMessages?.length ? 'whyFinderTextSummaryFiltered' : 'whyFinderTextSummary'
    }`;
    const data: { [prop: string]: any } = {
      temperature: 0.4,
      modelKey: 'gpt-4o-mini',
      messages: [...baseStateMessages, ...[message]],
      maxTokens: 1024,
      parameters: {
        system: [language, backgroundInfo],
      },
    };

    return this.http
      .post(url, data, {
        headers: this.setHeaders(reportKey, surveyKey),
        withCredentials: this.trackingAllowed || false,
      })
      .pipe(
        timeout(300000),
        catchError((error) => throwError(() => this.handleRequestError(error))),
      );
  }

  public getShortenedChartNames(surveyKey: string, reportKey: string, language: string, items: any[]) {
    const url: string = `https:${environment.apiServer}/ai/esko/chartNameShortener`;
    const data: { [prop: string]: any } = {
      temperature: 0.4,
      modelKey: 'gpt-4o-mini',
      functionName: 'getShortenedChartNames',
      messages: [
        {
          role: 'user',
          message: JSON.stringify(items),
        },
      ],
      parameters: {
        system: [language],
        function: [language],
      },
    };

    return this.http
      .post(url, data, {
        headers: this.setHeaders(reportKey, surveyKey),
        withCredentials: this.trackingAllowed || false,
      })
      .pipe(
        timeout(300000),
        catchError((error) => throwError(() => this.handleRequestError(error))),
      );
  }

  public getCrossNPSInsights(surveyKey: string, reportKey: string, language: string, items: any) {
    const url: string = `https:${environment.apiServer}/ai/esko/crossNPSInsights`;
    const data: { [prop: string]: any } = {
      temperature: 0.4,
      modelKey: 'gpt-4o-mini',
      maxTokens: 1024,
      messages: [
        {
          role: 'user',
          message: JSON.stringify(items),
        },
      ],
      parameters: {
        system: [language],
        function: [language],
      },
    };

    return this.http
      .post(url, data, {
        headers: this.setHeaders(reportKey, surveyKey),
        withCredentials: this.trackingAllowed || false,
      })
      .pipe(
        timeout(300000),
        catchError((error) => throwError(() => this.handleRequestError(error))),
      );
  }

  public getSummaryAveragesTextSummary(
    surveyKey: string,
    reportKey: string,
    properties: [
      language: string,
      averageNumber: string,
      questionCount: string,
      sliderScale: string,
      questionData: string,
      groupData: string,
      customInstructions: string,
    ],
    messages: { role: string; message: string }[] = [],
  ) {
    const url: string = `https:${environment.apiServer}/ai/esko/summaryAveragesTextSummary`;
    const data: { [prop: string]: any } = {
      temperature: 0.4,
      modelKey: 'vertexai-claude-3-5-sonnet@20240620',
      secondaryModelKey: 'gpt-4o-latest',
      messages,
      maxTokens: 1024,
      parameters: {
        user: properties,
      },
    };

    return this.http
      .post(url, data, {
        headers: this.setHeaders(reportKey, surveyKey),
        withCredentials: this.trackingAllowed || false,
      })
      .pipe(
        timeout(300000),
        map((res: any) => AIModelDependentMessage(res)),
        catchError((error) => throwError(() => this.handleRequestError(error))),
      );
  }

  // ### Helper functions ###
  public setHeaders(reportKey, surveyKey) {
    const teamKey = this.store.selectSnapshot(AccountState.teamKey);
    const authToken: string = this.store.selectSnapshot(AuthState.authToken);

    return new HttpHeaders()
      .set('Environment', environment.config)
      .set('Active-Team', teamKey || '')
      .set('Target-Report', reportKey || '')
      .set('Survey-Key', surveyKey || '')
      .set('Content-Type', 'application/json')
      .set('Firebase-Authorization', authToken || '');
  }

  public handleRequestError(error: any) {
    const message =
      error.error instanceof Error ? error.error.message : error.status || $localize`:@@zef-i18n-00015:Unknown Error`;

    this.dc.openErrorDialog($localize`:@@zef-i18n-00040:Server Error`, message.toString());

    return of(message);
  }
}
